import { apiUrl } from '../config/api';

export type AdminRole = 'super_user' | 'organizzatore' | 'redattore';

export interface AuthState {
  authenticated: boolean;
  username?: string;
  role?: AdminRole;
}

export class AuthService {
  private static cachedState: AuthState | null = null;
  private static pendingState: Promise<AuthState> | null = null;
  private readonly apiUrl = apiUrl('/api/auth');

  async login(username: string, password: string): Promise<AuthState> {
    const state = await this.request<AuthState>(`${this.apiUrl}/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password })
    });
    AuthService.cachedState = state;
    AuthService.pendingState = null;
    return state;
  }

  me(forceRefresh = false): Promise<AuthState> {
    if (!forceRefresh && AuthService.cachedState?.authenticated) {
      return Promise.resolve(AuthService.cachedState);
    }

    if (!forceRefresh && AuthService.pendingState) {
      return AuthService.pendingState;
    }

    AuthService.pendingState = this.request<AuthState>(`${this.apiUrl}/me`)
      .then((state) => {
        AuthService.cachedState = state;
        return state;
      })
      .catch((error) => {
        AuthService.cachedState = { authenticated: false };
        throw error;
      })
      .finally(() => {
        AuthService.pendingState = null;
      });

    return AuthService.pendingState;
  }

  async isAuthenticated(): Promise<boolean> {
    if (AuthService.cachedState?.authenticated) {
      return true;
    }

    try {
      const state = await this.me();
      return state.authenticated;
    } catch {
      return false;
    }
  }

  async logout(): Promise<void> {
    try {
      await this.request<AuthState>(`${this.apiUrl}/logout`, { method: 'POST' });
    } finally {
      AuthService.cachedState = { authenticated: false };
      AuthService.pendingState = null;
    }
  }

  private async request<T>(url: string, init?: RequestInit): Promise<T> {
    const response = await fetch(url, { cache: 'no-store', credentials: 'same-origin', ...init });
    const data = await response.json().catch(() => null);

    if (!response.ok) {
      throw new Error(data?.error ?? 'Errore autenticazione');
    }

    return data as T;
  }
}
